/**
 * Login page — email/password form.
 *
 * No registration link (invite-only: accounts are admin-created).
 * After successful login, redirects to the page the user was trying
 * to access, or to the home page by default.
 */

import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useAuth } from '@/hooks/useAuth'
import { getBranding } from '@/lib/branding'
import { useBrandLogo } from '@/hooks/useBrandLogo'
import {
  Button,
  Input,
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui'

export function LoginPage() {
  const { user, accessToken, mustChangePassword, impersonation } = useAuthStore()
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const branding = getBranding()
  const logoUrl = useBrandLogo()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // SA-906: under a view-as, go home — and never on to the target's password
  // page (their must-change-password is their own obligation, D7).
  if (impersonation) {
    return <Navigate to="/" replace />
  }

  // If already authenticated, redirect away
  if (user && accessToken) {
    if (mustChangePassword) {
      return <Navigate to="/change-password" replace />
    }
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'
    return <Navigate to={from} replace />
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await login(email, password)

      // Check if the user needs to change password
      const { mustChangePassword: mustChange } = useAuthStore.getState()
      if (mustChange) {
        navigate('/change-password', { replace: true })
      } else {
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'
        navigate(from, { replace: true })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-primary/5 to-primary/10 p-4">
      <div className="w-full max-w-md">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4">
            <img
              src={logoUrl}
              alt={branding.appName ?? 'Secret Agents AI'}
              translate="no"
              className="w-64 h-32 object-contain"
            />
          </div>
          {/* translate="no" (SA-247): brand name must survive browser
              page translation ("Jaso" auto-detects as Basque "to get"). */}
          <p className="text-muted-foreground" translate="no">
            {branding.appName ?? 'Secret Agents AI'}
          </p>
        </div>

        {/* Login Card */}
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Welcome back</CardTitle>
            <CardDescription className="text-center">
              Sign in to your account to continue
            </CardDescription>
          </CardHeader>

          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />

              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />

              <Button
                type="submit"
                className="w-full"
                isLoading={isSubmitting}
                disabled={!email || !password}
              >
                Sign in
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>
            Contact your administrator to request an account.
          </p>
          <p className="mt-1">
            &copy; {new Date().getFullYear()} Secret Agents AI. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
